import { localizeSystemText } from '../../i18n/messages';
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { ListGroup } from "react-bootstrap";
import { useTranslation } from 'react-i18next';

const ListItemSelector = ({ label, options, onSelect, selectedIDs, layout,
  disabled, emptyMessage, noResultsMessage, keyboardButtonText, searchLabel }) => {
  const { t: tr } = useTranslation();
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [filter, setFilter] = useState("");
  const searchInputRef = useRef(null);
  const isGridLayout = layout === "grid";

  const filterOptions = (filterValue = filter) => {
    const valueLower = filterValue.toLowerCase();
    return options.filter((o) => o.text.toLowerCase().includes(valueLower));
  };

  useEffect(() => {
    setFilteredOptions(filterOptions());
  }, [options, filter]);

  const handleChange = (event) => {
    const { value } = event.target;
    setFilter(value);
    setFilteredOptions(filterOptions(value));
  };

  const hideKeyboard = () => {
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
  };

  const renderButton = option => (
    <button
      type="button"
      className={`btn btn-block manage-button manage-teal-button ${
        selectedIDs.includes(option.value) ? "selected" : ""
      }`}
      onClick={() => onSelect(option)}
      disabled={disabled}
    >
      <span>{option.primaryText || option.text}</span>
      {option.secondaryText && <small className="list-item-selector-description">{option.secondaryText}</small>}
    </button>
  );

  const visibleOptions = isGridLayout ? options : filteredOptions;
  const emptyText = options.length === 0 ? emptyMessage : noResultsMessage;

  return (
    <div className="form-group">
      <span>{localizeSystemText(label, tr)}</span>
      <div className="field">
        {/* Note, value is set here rather than on the option - docs: https://facebook.github.io/react/docs/forms.html */}
        {!isGridLayout && (
          <div className="list-item-selector-search mb-3">
            <input
              ref={searchInputRef}
              type="text"
              name="search"
              className="form-control"
              value={filter}
              onChange={handleChange}
              placeholder={tr("common:search")}
              aria-label={localizeSystemText(searchLabel, tr)}
              disabled={disabled}
            />
            <button
              type="button"
              className="btn btn-sm manage-button manage-teal-button list-item-selector-keyboard-button"
              aria-label={keyboardButtonText === "Gotowe" ? tr("common:hideKeyboard") : localizeSystemText(keyboardButtonText, tr)}
              disabled={disabled}
              onClick={hideKeyboard}
            >
              {localizeSystemText(keyboardButtonText, tr)}
            </button>
          </div>
        )}

        <div className="">
          {visibleOptions.length === 0 && emptyText && <p role="status">{localizeSystemText(emptyText, tr)}</p>}
          {isGridLayout ? (
            <div className="list-item-selector-grid">
              {visibleOptions.map(option => (
                <div key={option.value} className="list-item-selector-grid-item">
                  {renderButton(option)}
                </div>
              ))}
            </div>
          ) : (
            <ListGroup
              variant="flush"
              className="list-item-selector-list"
            >
              {visibleOptions.map((option) => (
                <ListGroup.Item key={option.value} className="border-0">
                  <div className="row">
                    <div className="col-12">
                      {renderButton(option)}
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </div>
      </div>
    </div>
  );
};

ListItemSelector.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      primaryText: PropTypes.string,
      secondaryText: PropTypes.string,
    })
  ).isRequired,
  onSelect: PropTypes.func.isRequired,
  selectedIDs: PropTypes.arrayOf(PropTypes.string),
  layout: PropTypes.oneOf(["list", "grid"]),
  disabled: PropTypes.bool,
  emptyMessage: PropTypes.string,
  noResultsMessage: PropTypes.string,
  keyboardButtonText: PropTypes.string,
  searchLabel: PropTypes.string,
};

ListItemSelector.defaultProps = {
  options: [],
  selectedIDs: [],
  layout: "list",
  disabled: false,
  emptyMessage: "",
  noResultsMessage: "",
  keyboardButtonText: "Gotowe",
  searchLabel: "Szukaj",
};

export default ListItemSelector;
